<script>
import buildFinalIframeSrc from './utils/buildFinalIframeSrc';
import supportedEmbeds from './utils/supportedEmbeds';

export default {
  name: 'FacebookVideoEmbed',

  props: {
    src: {
      type: String,
      required: true,
    },

    muted: {
      type: Boolean,
      default: true,
    },

    loop: {
      type: Boolean,
      default: true,
    },

    autoplay: {
      type: Boolean,
      default: true,
    },

    videoPlaying: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'error',
    'durationchange',
    'ready',
    'play',
    'pause',
    'timeupdate',
    'ended',
  ],

  data() {
    return {
      network: 'facebook',
      paused: true,
      fbVideoDiv: null,
      player: null,
      timerInterval: null,
    };
  },

  computed: {
    finalSrc() {
      if (supportedEmbeds[this.network].regex.exec(this.src)) {
        return buildFinalIframeSrc(this.$props, supportedEmbeds[this.network]);
      }

      this.$emit('error', { message: 'Invalid or unsupported Facebook URL.' });
      return null;
    },

    duration() {
      if (this.player?.getDuration) {
        return this.player.getDuration();
      }

      return null;
    },

    currentTime: {
      get() {
        if (this.player) {
          return this.player.getCurrentPosition();
        }
        return null;
      },

      set(v) {
        if (this.player) {
          this.player.seek(v);
        }
      },
    },
  },

  watch: {
    videoPlaying(isPlaying) {
      if (isPlaying) {
        this.play();
      } else {
        this.pause();
      }
    },

    muted(newValue) {
      if (newValue) {
        this.mute();
      } else {
        this.unmute();
      }
    },

    src(newValue) {
      if (newValue) {
        this.initFacebook();
        this.facebookPlayerReady();
      }
    },
  },

  mounted() {
    this.loadFacebookSDK();
    this.observer = new MutationObserver(this.observerCallback);
    this.observer.observe(this.$refs.fbVideoDiv, { childList: true });
  },

  beforeUnmount() {
    this.observer.disconnect();
    clearInterval(this.timerInterval);
  },

  methods: {
    getDuration() {
      return this.duration;
    },

    startTimerInterval() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }

      this.timerInterval = setInterval(() => {
        if (this.player && !this.paused) {
          this.$emit('timeupdate', {
            network: this.network,
            target: this.player,
          });
        }
      }, 250);
    },

    play() {
      if (this.player) {
        this.player.play();
        this.startTimerInterval();
        this.paused = false;
        this.$emit('play', { network: this.network, target: this.player });
      }
    },

    pause() {
      if (this.player) {
        this.player.pause();
        clearInterval(this.timerInterval);
        this.paused = true;
        this.$emit('pause', { network: this.network, target: this.player });
      }
    },

    mute() {
      if (this.player) {
        this.player.mute();
      }
    },

    unmute() {
      if (this.player) {
        this.player.unmute();
      }
    },

    initFacebook() {
      window.FB.init({
        appId: '232298016899680', // TODO: figure out the best way to set this with ENV vars
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v2.9',
      });
    },

    facebookPlayerReady() {
      window.FB.Event.subscribe('xfbml.ready', (msg) => {
        if (msg.type === 'video') {
          this.player = msg.instance;
          const target = this.player;

          setTimeout(() => {
            this.$emit('ready', { network: this.network, target });
            this.$emit('durationchange', { network: this.network, target });
          }, 100);

          if (this.autoplay) {
            target.seek(0);
            target.play();
            this.paused = false;
            this.startTimerInterval();
            this.$emit('play', { network: this.network, target });
          } else {
            target.pause();
            target.seek(0);
            this.paused = true;
            this.$emit('pause', { network: this.network, target });
          }

          if (this.muted) {
            this.mute();
          } else {
            this.unmute();
          }

          target.subscribe('paused', () => {
            this.paused = true;
            this.$emit('pause', { network: this.network, target });
          });

          target.subscribe('finishedPlaying', () => {
            if (this.loop) {
              this.play();
            } else {
              this.$emit('ended', { network: this.network, target });
            }
          });
        }
      });
    },

    loadFacebookSDK() {
      if (!window.FB) {
        const tag = document.createElement('script');
        tag.src = 'https://connect.facebook.net/en_US/sdk.js';
        tag.async = true;
        tag.defer = true;
        tag.crossOrigin = 'anonymous';
        tag.nonce = '3wT0LBAQ';
        document.head.appendChild(tag);

        window.fbAsyncInit = () => {
          this.initFacebook();
          this.facebookPlayerReady();
        };
      } else {
        this.initFacebook();
        this.facebookPlayerReady();
      }
    },

    observerCallback(mutationList) {
      // Preserve the component library's observer behavior.
      // eslint-disable-next-line no-restricted-syntax
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          this.$refs.fbVideoDiv
            .querySelector('iframe')
            .classList.add(
              'tgb-iframe',
              'tgb-iframe--video-embed',
              'tgb-iframe--facebook',
            );
        }
      }
    },
  },
};
</script>

<template>
  <div
    ref="fbVideoDiv"
    class="fb-video"
    :data-href="finalSrc"
    data-autoplay="true"
    data-allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
    data-width="auto"
  />
</template>

<style scoped>
.fb-video {
  display: inline-grid;
  width: 100%;
  height: 100%;
  align-content: center;
}

.tgb-iframe--facebook {
  width: 100%;
  height: 100%;
}
</style>
